import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  Avatar,
  Container,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
} from "@mui/material";
import { AuthContext } from "./../../../AuthContext/AuthContext";
import { BASE_URL } from "./../../../Api/ApiUrls";
import { useNavigate } from "react-router-dom";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import FormsBottomNavbar from "./../../Post Module/FormsBottomNavbar";
import ActiveSubscription from "./ActiveSubscription";
import SubscriptionForm from "./SubscriptionForm";
import { RAZORPAY_URL } from "../../../Api/ApiUrls";
import { RAZORPAY_KEY } from "../../../Api/ApiUrls";
import GuestLoginModal from "../../../../Components/GuestLoginModal";
import SuccessAlert from '../../../../Components/Users/Alertpage/SuccessAlert/SuccessAlert';
import FailureAlert from '../../../../Components/Users/Alertpage/FailureAlert/FailureAlert';

// =========================================================
// THEME TOKENS (must match SubscriptionForm.js)
// =========================================================
const GOLD = "#A8752D";
const BLACK = "#111111";

const UserSubscriptionPanel = () => {
  const [selectedUserType, setSelectedUserType] = useState("");
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedAddon, setSelectedAddon] = useState(null);
  const [plans, setPlans] = useState([]);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showGuestModal, setShowGuestModal] = useState(false);
  const [subscriptions, setSubscriptions] = useState([]);
  const [tabValue, setTabValue] = useState(1); // Default to Subscription Plans tab
  const { userId, isGuest } = useContext(AuthContext);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showFailure, setShowFailure] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [iosPlans, setIosPlans] = useState([]);

  // ✅ listen for Apple plans injected by Expo
  useEffect(() => {
    const handler = (event) => {
      console.log("📦 Apple Plans Received:", event.detail);
      setIosPlans(event.detail || []);
    };
    window.addEventListener("APPLE_PLANS", handler);
    return () => window.removeEventListener("APPLE_PLANS", handler);
  }, []);

  // ✅ listen for successful purchase confirmation from Expo
  useEffect(() => {
    const handler = (event) => {
      console.log("✅ iOS Subscription activated:", event.detail);
      if (userId) {
        fetch(`${BASE_URL}/Get-all-plan/${userId}/`)
          .then((r) => r.json())
          .then((result) => {
            const data = Array.isArray(result) ? result : result.data || [];
            setSubscriptions(data);
          });
      }
      setAlertMessage("Subscription activated successfully!");
      setShowSuccess(true);
    };
    window.addEventListener("SUBSCRIPTION_ACTIVATED", handler);
    return () => window.removeEventListener("SUBSCRIPTION_ACTIVATED", handler);
  }, [userId]);

  const navigate = useNavigate();

  const requireLogin = (e, callback) => {
    e.stopPropagation();

    if (isGuest) {
      setShowGuestModal(true);
      return;
    }

    callback();
  };

  useEffect(() => {
    const fetchUserData = async () => {
      if (!userId) return;

      try {
        setLoading(true);
        const response = await fetch(`${BASE_URL}/users/${userId}/`);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setUserData(data);
        setError(null);
      } catch (err) {
        setError(err.message);
        console.error("Error fetching user data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [userId]);

  useEffect(() => {
    const fetchActiveSubscription = async () => {
      if (!userId) return;

      try {
        setLoading(true);
        const response = await fetch(`${BASE_URL}/Get-all-plan/${userId}/`);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        const data = Array.isArray(result) ? result : result.data || [];

        console.log("All subscriptions for user:", data);
        setSubscriptions(data);

        const activeSub = data.find((sub) => sub.status === true);

        if (activeSub) {
          setTabValue(1); // Switch to Subscription Plans tab
        }
        setError(null);
      } catch (err) {
        setError(err.message);
        console.error("Error fetching subscription data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchActiveSubscription();
  }, [userId]);

  // Fetch plans from API
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${BASE_URL}/subplans/`);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        setPlans(result);
        setError(null);
      } catch (err) {
        setError(err.message);
        console.error("Error fetching subscription plans:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  const handlePaymentSubmit = async (e, autoPayEnabled = false) => {
    e.preventDefault();
    setError(null);
    console.log("Please select a plan or addon before submitting:", selectedPlan, selectedAddon);

    if (!selectedPlan && !selectedAddon) {
      alert("Please select a plan or addon before submitting.");
      return;
    }

    try {
      if (selectedPlan) {
        // =========================================================
        // ✅ FREE PLAN → DIRECT ACTIVATE (NO RAZORPAY)
        // =========================================================
        if (selectedPlan?.plan_name === "Free") {
          try {
            const freePayload = {
              plan_id: selectedPlan.plan_id,
              user_id: userId,
            };
            console.log("free payload", freePayload);

            const response = await fetch(`${BASE_URL}/subscribe/free_plan_id/`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify(freePayload),
            });

            if (response.ok) {
              const data = await response.json();

              setSubscriptions((prev) => [...prev, data]);

              setAlertMessage("Free plan activated successfully!");
              setShowSuccess(true);

              return; // 🚀 STOP — Do not continue to Razorpay
            } else {
              const errorData = await response.text();
              console.error("Free plan activation failed:", errorData);

              setAlertMessage("Failed to activate free plan. Please try again.");
            }
            setShowFailure(true);
          } catch (error) {
            console.error("Free plan activation error:", error);

            setAlertMessage("An error occurred while activating free plan.");
            setShowFailure(true);
          }
        }

        // =========================================================
        // 💰 PAID PLAN → CREATE RAZORPAY SUBSCRIPTION
        // =========================================================

        let totalCount;

        if (autoPayEnabled) {
          const planName = selectedPlan.plan_name.toLowerCase();

          if (
            planName.includes("1 months") ||
            planName.includes("1-months") ||
            planName.includes("monthly")
          ) {
            totalCount = 12;
          } else if (
            planName.includes("3 months") ||
            planName.includes("3-months") ||
            planName.includes("quarterly")
          ) {
            totalCount = 4;
          } else if (
            planName.includes("6 months") ||
            planName.includes("6-months") ||
            planName.includes("half-yearly")
          ) {
            totalCount = 2;
          } else if (
            planName.includes("12 months") ||
            planName.includes("12-months") ||
            planName.includes("yearly") ||
            planName.includes("annual")
          ) {
            totalCount = 1;
          } else {
            totalCount = selectedPlan.total_count || 1;
          }
        } else {
          totalCount = 1;
        }
        const razorpayPayload = {
          plan_id: selectedPlan.razor_plan_id,
          customer_id: userData?.razor_user_id,
          total_count: totalCount,
          customer_notify: 1,
          notes: {
            plan_name: selectedPlan.plan_name,
            user_id: userId,
            auto_pay: autoPayEnabled,
          },
        };

        console.log(
          "Creating subscription with AutoPay:",
          autoPayEnabled,
          "Total count:",
          totalCount,
        );

        const razorRes = await fetch(
          `${RAZORPAY_URL}/api/razorpay/subscription`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(razorpayPayload),
          },
        );

        const razorData = await razorRes.json();
        if (!razorData.success) {
          throw new Error(
            razorData.error || "Failed to create Razorpay subscription",
          );
        }

        const subscription = razorData.subscription;
        const subscriptionId = subscription.id;

        const options = {
          key: RAZORPAY_KEY,
          subscription_id: subscriptionId,
          name: "Property Pincoder",
          description: `${selectedPlan.plan_name}${autoPayEnabled ? " (AutoPay Enabled)" : ""}`,
          handler: async function (response) {
            console.log("Payment response:", response);

            const verifyRes = await fetch(
              `${RAZORPAY_URL}/api/razorpay/verify-subscription-payment`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_subscription_id: response.razorpay_subscription_id,
                  razorpay_signature: response.razorpay_signature,
                }),
              },
            );

            const verifyData = await verifyRes.json();
            console.log("Verification response:", verifyData);

            if (!verifyData.success) {
              alert(`Payment verification failed: ${verifyData.error}`);
              return;
            }

            const dbPayload = {
              user_id: userId,
              plan_name: selectedPlan.plan_name,
              user_type: selectedPlan.user_type,
              charges: Number(selectedPlan.charges),
              reward_amount: selectedPlan.reward_amount
                ? Number(selectedPlan.reward_amount)
                : 0,
              pay_amount: selectedPlan.charges,
              buyer_no_unlimited: selectedPlan.buyer_no_unlimited || false,
              buyer_no: selectedPlan.buyer_no_unlimited
                ? null
                : Number(selectedPlan.buyer_no),
              no_of_properties_unlimited:
                selectedPlan.no_of_properties_unlimited || false,
              no_of_liked_data_unlimited:
                selectedPlan.no_of_liked_data_unlimited || false,
              matching_enquiry_unlimited:
                selectedPlan.matching_enquiry_unlimited || false,
              no_of_properties: selectedPlan.no_of_properties_unlimited
                ? null
                : Number(selectedPlan.no_of_properties),
              no_of_liked_data: selectedPlan.no_of_liked_data_unlimited
                ? null
                : Number(selectedPlan.no_of_liked_data),
              matching_enquiry: selectedPlan.matching_enquiry_unlimited
                ? null
                : Number(selectedPlan.matching_enquiry),
              status: true,
              sub_type: "New",
              razor_plan_id: selectedPlan.razor_plan_id,
              razor_user_id: userData?.razor_user_id,
              razor_subscription_id: subscriptionId,
              auto_pay: autoPayEnabled,
            };

            console.log("Saving to local DB:", dbPayload);

            try {
              const saveRes = await fetch(`${BASE_URL}/subusers/`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(dbPayload),
              });

              if (!saveRes.ok) {
                throw new Error(`HTTP error! status: ${saveRes.status}`);
              }

              const saveData = await saveRes.json();

              setSubscriptions((prev) => [...prev, saveData]);
              alert(
                `Plan activated successfully!${autoPayEnabled ? " AutoPay is enabled." : ""}`,
              );
              navigate("/work-detail");
            } catch (dbError) {
              console.error("Error saving to local DB:", dbError);
              alert(
                `Plan activated but failed to save locally: ${dbError.message}`,
              );
            }
          },
          prefill: {
            name: userData?.username || "User",
            email: userData?.email || "",
            contact: userData?.mobile_no || "",
          },
          theme: { color: "#3399cc" },
        };

        const rzp = new window.Razorpay(options);
        rzp.on("payment.failed", function (response) {
          console.error("Payment failed:", response.error);
          alert(`Payment failed: ${response.error.description}`);
        });
        rzp.open();
        return;
      }

      // ---------------------------------------------------------
      // ADDON → Normal payment flow (order → payment → verify → DB)
      // ---------------------------------------------------------
      if (selectedAddon) {
        const addonCharges = Number(selectedAddon.charges);
        const addonGST = Math.round(addonCharges * 0.18);
        const addonTotalWithGST = addonCharges + addonGST;

        const orderRes = await fetch(`${RAZORPAY_URL}/api/razorpay/orders`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            amount: addonTotalWithGST * 100,
            currency: "INR",
          }),
        });

        const orderData = await orderRes.json();
        if (!orderData.order_id) {
          throw new Error("Failed to create payment order");
        }

        const options = {
          key: RAZORPAY_KEY,
          amount: orderData.amount,
          currency: orderData.currency,
          name: "Property Pincoder",
          description: `Addon Purchase - ${selectedAddon.plan_name || "Addon"}`,
          order_id: orderData.order_id,

          handler: async function (response) {
            console.log("Addon Payment Success:", response);

            const verifyRes = await fetch(
              `${RAZORPAY_URL}/api/razorpay/verify-payment`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(response),
              }
            );

            const verifyData = await verifyRes.json();
            if (!verifyData.success) {
              alert("Payment verification failed!");
              return;
            }

            const payload = {
              user_id: userId,
              user_type: selectedAddon.user_type,
              charges: addonCharges,
              pay_amount: addonTotalWithGST,
              reward_amount: selectedAddon.reward_amount
                ? Number(selectedAddon.reward_amount)
                : 0,
              buyer_no_unlimited: selectedAddon.buyer_no_unlimited || false,
              buyer_no: selectedAddon.buyer_no_unlimited
                ? null
                : Number(selectedAddon.buyer_no),
              no_of_properties_unlimited: selectedAddon.no_of_properties_unlimited || false,
              no_of_liked_data_unlimited: selectedAddon.no_of_liked_data_unlimited || false,
              matching_enquiry_unlimited: selectedAddon.matching_enquiry_unlimited || false,
              no_of_properties: selectedAddon.no_of_properties_unlimited
                ? null
                : Number(selectedAddon.no_of_properties),
              no_of_liked_data: selectedAddon.no_of_liked_data_unlimited
                ? null
                : Number(selectedAddon.no_of_liked_data),
              matching_enquiry: selectedAddon.matching_enquiry_unlimited
                ? null
                : Number(selectedAddon.matching_enquiry),
              status: true,
            };

            const addonRes = await fetch(`${BASE_URL}/user-addon/`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(payload),
            });

            if (!addonRes.ok) {
              throw new Error("Addon save failed");
            }

            alert("Addon purchased successfully!");
            navigate("/work-detail");
          },

          prefill: {
            name: userData?.username || "User",
            email: userData?.email || "",
            contact: userData?.mobile_no || "",
          },
          theme: { color: "#3399cc" },
        };

        const rzp = new window.Razorpay(options);

        rzp.on("payment.failed", function (response) {
          console.error("Addon Payment Failed:", response.error);
          alert(`Payment failed: ${response.error.description}`);
        });

        rzp.open();
        return;
      }
    } catch (err) {
      console.error("Payment error:", err);
      setError(err.message);
      alert(`Payment failed: ${err.message}`);
    }
  };

  const handleTabChange = (newValue) => {
    if (isGuest && newValue === 0) {
      setShowGuestModal(true);
      return;
    }
    setTabValue(newValue);
  };

  if (loading) {
    return (
      <Box
        sx={{
          background: "#FAFAFA",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: 3,
        }}
      >
        <CircularProgress size={40} thickness={5} sx={{ color: GOLD }} />
        <Typography variant="h7" sx={{ color: "#000000", fontWeight: 600 }}>
          Loading User Subscription...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 2 }}>
        <Alert severity="error">Error: {error}</Alert>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        overflow: "hidden",
        background: "#FAFAFA",
      }}
    >
      {/* =====================================================
          FIXED HEADER WITH TABS
      ===================================================== */}
      <Box sx={{ flexShrink: 0 }}>
        <Box
          sx={{
            width: "100%",
            height: { xs: 54, sm: 78, md: 90 },
            background: BLACK,
            color: "#FFFFFF",
            display: "flex",
            alignItems: "center",
            px: { xs: 1, sm: 2.5, md: 3 },
            boxSizing: "border-box",
          }}
        >
          <ArrowBackIosNewIcon
            onClick={() => navigate(-1)}
            sx={{
              fontSize: { xs: 18, sm: 22, md: 24 },
              cursor: "pointer",
            }}
          />

          <Box sx={{ flex: 1 }} />

          <Avatar
            alt={userData?.username || "User"}
            src={userData?.profile ? `${BASE_URL}${userData.profile}` : ""}
            sx={{
              width: { xs: 36, sm: 48, md: 54 },
              height: { xs: 36, sm: 48, md: 54 },
              background: "#E6D5C0",
              color: "#FFFFFF",
              border: "1px solid #FFFFFF",
              flexShrink: 0,
            }}
          />

          <Box sx={{ ml: { xs: 0.6, sm: 1.2 }, width: { xs: 100, sm: 140, md: 160 } }}>
            <Typography
              sx={{
                fontSize: { xs: 11, sm: 14, md: 16 },
                fontWeight: 700,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {isGuest
                ? "Guest"
                : userData?.username
                  ? userData.username.split(" ").map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ")
                  : "Loading..."}
            </Typography>

            <Typography sx={{ fontSize: { xs: 8, sm: 11, md: 13 }, color: "#D0D0D0", mt: 0.2, whiteSpace: "nowrap" }}>
              {isGuest
                ? "GUEST USER"
                : userData?.user_id
                  ? `CUSTOMER${userData.user_id.toString().padStart(4, "0")}`
                  : "Loading..."}
            </Typography>
          </Box>
        </Box>

        <Tabs
          value={tabValue}
          onChange={(e, newValue) => handleTabChange(newValue)}
          variant="fullWidth"
          sx={{
            mt: 1,
            borderRadius: "40px",
            overflow: "hidden",
            boxShadow: "0 5px 10px rgba(0,0,0,0.1)",
            backgroundColor: "transparent",
            "& .MuiTabs-indicator": { display: "none" },
          }}
        >
          {/* TAB 1: Active Subscription */}
          <Tab
            label="Active Subscription"
            onClick={(e) => {
              if (isGuest) {
                e.preventDefault();
                e.stopPropagation();
                setShowGuestModal(true);
              }
            }}
            sx={{
              zIndex: 1,
              py: 2,
              cursor: isGuest ? "not-allowed" : "pointer",
              // Active: Solid #8A6A3D | Inactive: Original dark textured background
              background: tabValue === 0
                ? "#8A6A3D"
                : `linear-gradient(145deg, rgb(22, 22, 22), rgb(15, 15, 15)), url('/construction5.png')`,
              backgroundBlendMode: tabValue === 0 ? "normal" : "overlay",
              // Active: White text | Inactive: Light text
              color: tabValue === 0 ? "#FFFFFF !important" : "rgba(255,255,255,0.9) !important",
              fontWeight: 700,
              fontSize: "0.8rem",
              textTransform: "none",
              fontFamily: "Inter, Roboto, Helvetica, sans-serif",
              borderRight: "1px solid rgba(0,0,0,0.1)",
              borderTopLeftRadius: "30px",
              borderBottomLeftRadius: "30px",
              transition: "all 0.3s ease",
              position: "relative",
              "&:hover": { transform: "scale(1.02)" },
              "&::after": {
                content: '""',
                position: "absolute",
                bottom: 0,
                left: "50%",
                transform: "translateX(-50%)",
                width: tabValue === 0 ? "70%" : "0%",
                height: "3px",
                // Active: White indicator | Inactive: Original white indicator
                backgroundColor: "#FFFFFF",
                borderRadius: "3px",
                transition: "all 0.3s ease",
              },
            }}
          />

          {/* TAB 2: Subscription Plans */}
          <Tab
            label="Subscription Plans"
            sx={{
              zIndex: 1,
              py: 2,
              // Active: Solid #8A6A3D | Inactive: Original dark textured background
              background: tabValue === 1
                ? "#8A6A3D"
                : `linear-gradient(145deg, rgb(22, 22, 22), rgb(15, 15, 15)), url('/construction5.png')`,
              backgroundBlendMode: tabValue === 1 ? "normal" : "overlay",
              // Active: White text | Inactive: Light text
              color: tabValue === 1 ? "#FFFFFF !important" : "rgba(255,255,255,0.9) !important",
              fontWeight: 700,
              fontSize: "0.8rem",
              textTransform: "none",
              fontFamily: "Inter, Roboto, Helvetica, sans-serif",
              borderTopRightRadius: "30px",
              borderBottomRightRadius: "30px",
              transition: "all 0.3s ease",
              position: "relative",
              "&:hover": { transform: "scale(1.02)" },
              "&::after": {
                content: '""',
                position: "absolute",
                bottom: 0,
                left: "50%",
                transform: "translateX(-50%)",
                width: tabValue === 1 ? "70%" : "0%",
                height: "3px",
                // Active: White indicator | Inactive: Original white indicator
                backgroundColor: "#FFFFFF",
                borderRadius: "3px",
                transition: "all 0.3s ease",
              },
            }}
          />
        </Tabs>
      </Box>

      {/* =====================================================
          SCROLLABLE BODY
      ===================================================== */}
      <Box sx={{ flex: 1, overflowY: "auto", pb: "80px" }}>
        {tabValue === 0 ? (
          <ActiveSubscription
            subscriptions={subscriptions}
            userData={userData}
            navigate={navigate}
            plans={plans}
          />
        ) : (
          <SubscriptionForm
            selectedUserType={selectedUserType}
            setSelectedUserType={setSelectedUserType}
            selectedPlan={selectedPlan}
            setSelectedPlan={setSelectedPlan}
            selectedAddon={selectedAddon}
            setSelectedAddon={setSelectedAddon}
            plans={plans}
            handlePaymentSubmit={handlePaymentSubmit}
            subscriptions={subscriptions}
            iosPlans={iosPlans}
            userId={userId}
          />
        )}
      </Box>

      {/* =====================================================
          FIXED BOTTOM NAV
      ===================================================== */}
      <Box sx={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 1000 }}>
        <FormsBottomNavbar />
      </Box>

      <GuestLoginModal open={showGuestModal} onClose={() => setShowGuestModal(false)} />

      {showSuccess && (
        <SuccessAlert
          message={alertMessage}
          onClose={() => {
            setShowSuccess(false);
            navigate("/work-detail");
          }}
        />
      )}

      {showFailure && (
        <FailureAlert
          message={alertMessage}
          onClose={() => {
            console.log("Closing failure alert");
            setShowFailure(false);
            setAlertMessage("");
            navigate("/user-subscription");
          }}
        />
      )}
    </Box>
  );
};

export default UserSubscriptionPanel;